import * as React from 'react';
import { apiError } from '@/lib/api-error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Paperclip, Send, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import AttachmentView from '@/components/chat/AttachmentView';
import ChatAvatar from '@/components/chat/ChatAvatar';
import { fetchReportThread, sendReportMessage } from '@/data/advisor-chat';
import type { ReportMessage } from '@/types/chat';

export const REPORT_POLL_MS = 6000;
const MAX_FILES = 5;
const MAX_BYTES = 25 * 1024 * 1024;


function Bubble({ message, mine }: { message: ReportMessage; mine: boolean }) {
  return (
    <div className={`flex items-end gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
      {!mine && (
        <ChatAvatar
          name={message.sender_name || 'BitSwan'}
          photoUrl={message.sender_avatar_url}
          className="mb-4 h-7 w-7"
        />
      )}
      <div className="max-w-[85%] space-y-1">
        {!mine && (
          <p className="px-1 text-[11px] text-muted-foreground">
            {message.sender_name || 'BitSwan'}
          </p>
        )}
        <div
          className={`whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm ${
            mine ? 'bg-primary text-primary-foreground' : 'bg-muted'
          }`}
          data-testid="report-message"
        >
          {message.body}
          {message.attachments.length > 0 && (
            <div
              className={`mt-2 space-y-2 ${message.body ? 'border-t pt-2' : ''} ${
                mine ? 'border-primary-foreground/20' : 'border-border'
              }`}
            >
              {message.attachments.map((attachment) => (
                <AttachmentView key={attachment.id} attachment={attachment} />
              ))}
            </div>
          )}
        </div>
        <p className={`px-1 text-[10px] text-muted-foreground ${mine ? 'text-right' : ''}`}>
          {new Date(message.created_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
}

export default function ReportThread({ reportId }: { reportId: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState('');
  const [files, setFiles] = React.useState<File[]>([]);
  const fileInput = React.useRef<HTMLInputElement>(null);
  const bottom = React.useRef<HTMLDivElement>(null);

  const thread = useQuery({
    queryKey: ['report-thread', reportId],
    queryFn: () => fetchReportThread(reportId),
    refetchInterval: REPORT_POLL_MS,
    retry: false,
  });

  const messages = thread.data?.messages ?? [];
  const report = thread.data?.report;

  React.useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  React.useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['advisor-report-options'] });
    queryClient.invalidateQueries({ queryKey: ['chat-unread'] });
  }, [messages.length, queryClient]);

  React.useEffect(() => {
    setDraft('');
    setFiles([]);
  }, [reportId]);

  const send = useMutation({
    mutationFn: () => sendReportMessage(reportId, draft.trim(), files),
    onSuccess: () => {
      setDraft('');
      setFiles([]);
      queryClient.invalidateQueries({ queryKey: ['report-thread', reportId] });
      queryClient.invalidateQueries({ queryKey: ['advisor-report-options'] });
    },
    onError: (error) => toast.error(apiError(error, 'Could not send that.')),
  });

  const pickFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = '';
    const tooBig = picked.find((f) => f.size > MAX_BYTES);
    if (tooBig) {
      toast.error(`${tooBig.name} is larger than 25 MB.`);
      return;
    }
    if (files.length + picked.length > MAX_FILES) {
      toast.warning(`Up to ${MAX_FILES} files per message.`);
    }
    setFiles([...files, ...picked].slice(0, MAX_FILES));
  };

  const canSend = (draft.trim().length > 0 || files.length > 0) && !send.isPending;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="report-thread">
      {report && (
        <div className="border-b bg-muted/40 px-4 py-3">
          <p className="flex items-center gap-1.5 text-xs font-medium">
            <ShieldAlert className="h-3.5 w-3.5 text-red-600" />
            Your report about {report.advisor_name}
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {report.category_display} · {report.status_display} · filed{' '}
            {new Date(report.created_at).toLocaleDateString()}
          </p>
          <p
            className="mt-2 whitespace-pre-wrap rounded-md bg-background px-3 py-2 text-xs"
            data-testid="report-thread-details"
          >
            {report.details}
          </p>
        </div>
      )}

      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="report-messages">
        {thread.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <p
            className="py-8 text-center text-sm text-muted-foreground"
            data-testid="report-no-messages"
          >
            No replies yet. Add anything else that would help, and BitSwan will reply here. Your advisor cannot see any of this.
          </p>
        ) : (
          messages.map((message) => (
            <Bubble key={message.id} message={message} mine={message.side === 'customer'} />
          ))
        )}
        <div ref={bottom} />
      </div>

      <div className="space-y-2 border-t p-3">
        {files.length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid="report-pending-attachments">
            {files.map((file, index) => (
              <Badge key={`${file.name}-${index}`} variant="outline" className="gap-1">
                <Paperclip className="h-3 w-3" />
                <span className="max-w-[140px] truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => setFiles(files.filter((_, i) => i !== index))}
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && canSend) {
              e.preventDefault();
              send.mutate();
            }
          }}
          placeholder="Anything else BitSwan should know…"
          rows={2}
          className="resize-none"
          data-testid="report-input"
        />
        <div className="flex items-center gap-2">
          <input
            ref={fileInput}
            type="file"
            multiple
            className="hidden"
            onChange={pickFiles}
            data-testid="report-file-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInput.current?.click()}
            data-testid="report-attach"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            className="ml-auto"
            onClick={() => send.mutate()}
            disabled={!canSend}
            data-testid="report-send"
          >
            {send.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-1 h-4 w-4" />
            )}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
